import { useEffect, useRef, useState } from 'react';

import { differenceInHours, differenceInMinutes, differenceInSeconds, parseISO } from 'date-fns';

const INTERVAL_MS = 1000;

export type CountdownValues = { hours: number; minutes: number; seconds: number; isFinished: boolean };

const computeCountdown = (targetDate: string | null): CountdownValues => {
  if (!targetDate) return { hours: 0, minutes: 0, seconds: 0, isFinished: true };

  const target = parseISO(targetDate);
  const now = new Date();
  const totalSeconds = differenceInSeconds(target, now);

  if (totalSeconds <= 0) return { hours: 0, minutes: 0, seconds: 0, isFinished: true };

  return {
    hours: differenceInHours(target, now),
    minutes: differenceInMinutes(target, now) % 60,
    seconds: totalSeconds % 60,
    isFinished: false,
  };
};

export const useCountdown = (targetDate: string | null, onFinished?: () => void): CountdownValues => {
  const [value, setValue] = useState(() => computeCountdown(targetDate));
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    const current = computeCountdown(targetDate);
    setValue(current);

    if (!targetDate || current.isFinished) return;

    const interval = setInterval(() => {
      const updated = computeCountdown(targetDate);
      setValue(updated);

      if (updated.isFinished) {
        clearInterval(interval);
        onFinishedRef.current?.();
      }
    }, INTERVAL_MS);

    return () => clearInterval(interval);
  }, [targetDate]);

  return value;
};
